import React, { useCallback, useMemo, useState } from 'react';
import { SAutoComplete } from 'components/Page/Header/TopHeader/Profile/DelegateSessionConnectionBox/ListAccounts.styled';
import { Flex, Text } from 'UI';

export interface AccountListItem {
  tresorCode: string;
  name: string;
  primaryClientCode?: string;
}

interface ListAccountsProps {
  labelInputPlaceholder: string;
  data: AccountListItem[];
  'data-cy'?: string;
  onSelect: (item: AccountListItem) => void;
  notFoundLabel: string;
}

export const fmt = (name?: string, bir?: string, clientCode?: string) =>
  [name, bir, clientCode].filter(Boolean).join(' - ');

export const ListAccounts = ({
  labelInputPlaceholder,
  data,
  'data-cy': dataCy,
  onSelect,
  notFoundLabel,
}: ListAccountsProps) => {
  const [filter, setFilter] = useState<string>('');
  const filterData = useMemo(() => {
    return data.filter((a) => {
      if (filter.length === 0) {
        return true;
      }

      const normalizedFilter = filter.toLowerCase();
      return (
        a.tresorCode.includes(filter) ||
        a.name.toLowerCase().includes(normalizedFilter) ||
        Boolean(a.primaryClientCode?.includes(filter))
      );
    });
  }, [data, filter]);
  const options = useMemo(
    () =>
      filterData.map((item) => ({
        value: item.tresorCode,
        label: fmt(item.name, item.tresorCode, item.primaryClientCode),
      })),
    [filterData],
  );
  const hasFilterError = filter.length > 0 && filterData.length === 0;
  const handleSelect = useCallback(
    (value: string) => {
      const selectedItem = data.find((item) => item.tresorCode === value);
      if (selectedItem) {
        onSelect(selectedItem);
      }
    },
    [data, onSelect],
  );

  return (
    <Flex direction={'column'} padding={'12px 24px'}>
      <SAutoComplete
        placeholder={labelInputPlaceholder}
        value={filter}
        onChange={setFilter}
        onSelect={handleSelect}
        options={options}
        filterOption={false}
        status={hasFilterError ? 'error' : undefined}
        variant={'outlined'}
        data-cy={dataCy}
      />
      {hasFilterError && (
        <Text type={'base'} color={'palette_red_8'}>
          {notFoundLabel}
        </Text>
      )}
    </Flex>
  );
};
